/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {forwardRef} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Flex,
    useRadio,
    useRadioGroup,
    useStyleConfig,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

export const RadioButton = forwardRef((props, ref) => {
    const {children, ...radioProps} = props

    const styles = useMultiStyleConfig('RadioButton')
    const {getInputProps, getRadioProps} = useRadio(radioProps)

    return (
        <Box as="label" {...styles.label}>
            <input {...getInputProps({ref})} hidden />
            <Box {...getRadioProps()} aria-hidden={false} {...styles.button}>
                {children}
            </Box>
        </Box>
    )
})

/**
 * A component that renders and manages a group of radio buttons.
 * Can be uncontrolled, with internal state
 * Can be controlled by react-hook-form's Controller
 */
export const RadioButtonGroup = forwardRef((props, ref) => {
    const {options, ...radioGroupProps} = props

    const styles = useStyleConfig('RadioButtonGroup')
    const {getRootProps, getRadioProps} = useRadioGroup(radioGroupProps)

    return (
        <Flex gap={16} {...getRootProps()} {...styles.list}>
            {options.map((option) => {
                const radioProps = getRadioProps({value: String(option.value)})
                return (
                    <RadioButton key={option.value} {...radioProps} ref={ref}>
                        {option.label}
                    </RadioButton>
                )
            })}
        </Flex>
    )
})

RadioButton.displayName = 'RadioButton'
RadioButton.propTypes = {
    children: PropTypes.any,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onChange: PropTypes.func.isRequired
}

RadioButtonGroup.displayName = 'RadioButtonGroup'
RadioButtonGroup.propTypes = {
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label: PropTypes.string.isRequired
        })
    ).isRequired
}
